import React, { useState, useRef, useEffect } from 'react';
import SignatureModal from '../SignatureModal';

interface TelehealthConsentProps {
  onAccept: (signature: string) => void;
}

const TelehealthConsent: React.FC<TelehealthConsentProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    setSignature(signatureData);
    setShowSignatureModal(false);
  };

  const handleSubmit = () => {
    if (signature) {
      onAccept(signature);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapists of Atlanta
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main St., Palmetto, GA. 30268 | 470.250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            Consent for Telehealth Consultation
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please read the consent form below and scroll to the bottom to continue
          </p>
        </div>

        {/* Consent Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          marginBottom: '2rem'
        }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              height: '400px',
              overflowY: 'auto',
              padding: '2rem',
              color: '#d1d5db',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}
          >
            <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>
              CONSENT FOR TELEHEALTH CONSULTATION
            </h2>

            <ol style={{ marginBottom: '2rem', paddingLeft: '1.5rem', color: '#d1d5db' }}>
              <li style={{ marginBottom: '1rem' }}>
                I understand that my health care provider wishes me to engage in a telehealth consultation.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                My health care provider explained to me how the video conferencing technology that will be used to affect such a consultation will not be the same as a direct client/health care provider visit due to the fact that I will not be in the same room as my provider.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                I understand that telehealth consultation has potential benefits including easier access to care and the convenience of meeting from a location of my choosing.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                I understand there are potential risks to this technology, including interruptions, unauthorized access, and technical difficulties. I understand that my health care provider or I can discontinue the telehealth consult/visit if it is felt that the videoconferencing connections are not adequate for the situation.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                I have had a direct conversation with my provider, during which I had the opportunity to ask questions in regard to this procedure. My questions have been answered and the risks, benefits and any practical alternatives have been discussed with me in a language in which I understand.
              </li>
            </ol>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              CONSENT TO USE THE TELEHEALTH BY MYCLINICCORE SERVICE
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Telehealth by MyClinicCore is the technology service we will use to conduct telehealth videoconferencing appointments. It is simple to use and there are no passwords required to log in. By signing this document, I acknowledge:
            </p>

            <ol style={{ marginBottom: '2rem', paddingLeft: '1.5rem', color: '#d1d5db' }}>
              <li style={{ marginBottom: '1rem' }}>
                Telehealth by MyClinicCore is NOT an Emergency Service and in the event of an emergency, I will use a phone to call 911.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                Though my provider and I may be in direct, virtual contact through the Telehealth Service, neither MyClinicCore nor the Telehealth Service provides any medical or healthcare services or advice including, but not limited to, emergency or urgent medical services.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                The Telehealth by MyClinicCore Service facilitates videoconferencing and is not responsible for the delivery of any healthcare, medical advice or care.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                I do not assume that my provider has access to any or all of the technical information in the Telehealth by MyClinicCore Service – or that such information is current, accurate or up-to-date. I will not rely on my health care provider to have any of this information in the Telehealth by MyClinicCore Service.
              </li>
              <li style={{ marginBottom: '1rem' }}>
                To maintain confidentiality, I will not share my telehealth appointment link with anyone unauthorized to attend the appointment.
              </li>
            </ol>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              CERTIFICATION
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              By signing this form, I certify:
            </p>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', color: '#d1d5db' }}>
              <li style={{ marginBottom: '0.5rem' }}>That I have read or had this form read and/or had this form explained to me.</li>
              <li style={{ marginBottom: '0.5rem' }}>That I fully understand its contents including the risks and benefits of the procedure(s).</li>
              <li style={{ marginBottom: '0.5rem' }}>That I have been given ample opportunity to ask questions and that any questions have been answered to my satisfaction.</li>
            </ul>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', color: '#f9fafb', textTransform: 'uppercase' }}>
              BY SIGNING BELOW I AM AGREEING THAT I HAVE READ, UNDERSTOOD AND AGREE TO THE ITEMS CONTAINED IN THIS DOCUMENT.
            </p>

            <p style={{ marginBottom: '0', fontSize: '0.8rem', color: '#d1d5db' }}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Agreement Section */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #6CA6C1',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: hasScrolledToBottom ? '#f9fafb' : '#6b7280',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
            }}>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
                disabled={!hasScrolledToBottom}
                style={{
                  marginRight: '0.75rem',
                  width: '18px',
                  height: '18px',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              />
              I have read, understood, and agree to the Telehealth Consent
            </label>
          </div>

          {/* Signature Section */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: isAgreed ? '#f9fafb' : '#6b7280',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>
              Digital Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureModal(true)}
              disabled={!isAgreed}
              style={{
                width: '100%',
                padding: '3rem 1rem',
                borderRadius: '8px',
                border: '2px dashed #6CA6C1',
                background: isAgreed ? '#374151' : '#1f2937',
                color: isAgreed ? '#f9fafb' : '#6b7280',
                fontSize: '1rem',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              {signature ? (
                <div>
                  <div style={{ marginBottom: '0.5rem', color: '#10b981' }}>✓ Signature Captured</div>
                  <div style={{ fontSize: '0.875rem' }}>Click to update signature</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>📝</div>
                  <div>Click to add your digital signature</div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!signature}
            style={{
              background: signature
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: signature ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: signature
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none'
            }}
          >
            Submit Consent
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress:
            <span style={{ color: hasScrolledToBottom ? '#10b981' : '#6b7280', marginLeft: '0.5rem' }}>
              ✓ Read Consent
            </span>
            <span style={{ color: isAgreed ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Accept Terms
            </span>
            <span style={{ color: signature ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Digital Signature
            </span>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          title="Telehealth Consent Signature"
          clientName="Client"
          previousSignature={signature ? {
            signatureData: signature,
            signatureType: 'drawn' as const
          } : undefined}
        />
      )}
    </div>
  );
};

export default TelehealthConsent;